import { useMemo } from 'react'
import { IPassportReference } from '@obvious.tech/constellation'
import Loadable, { TLoadable } from 'src/utils/Loadable'

export type TPassportReference = {
  _tag: 'Passport'
  id: string
  username: string
}

export type TGroupReference = {
  _tag: 'Group'
  id?: string
  name?: string
}

export type TAssignedEntity = TPassportReference | TGroupReference

export type TUseAlarmAssignedEntitiesResult = TLoadable<TAssignedEntity[]>

export type TResolvedGroup = { id?: string; name: string }

export type TAssignedEntitiesInput = {
  assignedPassports: IPassportReference[] | undefined
  assignedGroups: string[] | undefined
  legacyAssignedPassport: IPassportReference | undefined
}

export const isPassport = (it: TAssignedEntity): it is TPassportReference =>
  it._tag === 'Passport'

export const isGroup = (it: TAssignedEntity): it is TGroupReference =>
  it._tag === 'Group'

export const resolveAssignedEntities = ({
  assignedPassports,
  assignedGroups,
  legacyAssignedPassport,
}: TAssignedEntitiesInput): TUseAlarmAssignedEntitiesResult => {
  const passports = dedupeBy(
    (assignedPassports ?? []).map(toPassport).filter(isNotNull),
    it => it.id,
  )

  const groups = dedupeBy(
    (assignedGroups ?? []).filter(isNonEmptyString).map(toGroup),
    it => it.name ?? it.id ?? '',
  )

  if (passports.length > 0 || groups.length > 0)
    return Loadable.successW([...passports, ...groups])

  return resolveLegacyAssignedEntity(legacyAssignedPassport)
}

export const pendingGroupIds = (
  assignedEntities: TUseAlarmAssignedEntitiesResult,
): string[] =>
  (assignedEntities.data ?? [])
    .filter(isGroup)
    .flatMap(it => (needsLookup(it) ? [it.id] : []))

export const applyGroupNames = (
  assignedEntities: TUseAlarmAssignedEntitiesResult,
  groups: TLoadable<TResolvedGroup[], NonNullable<unknown>>,
): TUseAlarmAssignedEntitiesResult => {
  if (assignedEntities.data === null) return assignedEntities
  if (pendingGroupIds(assignedEntities).length === 0) return assignedEntities

  if (groups.loaded === false) return Loadable.loading()

  const nameById = new Map(
    (groups.data ?? []).map(group => [group.id, group.name]),
  )

  return Loadable.successW(
    assignedEntities.data.map(entity =>
      isGroup(entity) && needsLookup(entity)
        ? { ...entity, name: nameById.get(entity.id) }
        : entity,
    ),
  )
}

export const useAlarmAssignedEntities = ({
  assignedPassports,
  assignedGroups,
  legacyAssignedPassport,
}: TAssignedEntitiesInput): TUseAlarmAssignedEntitiesResult => {
  return useMemo(
    () =>
      resolveAssignedEntities({
        assignedPassports,
        assignedGroups,
        legacyAssignedPassport,
      }),
    [assignedPassports, assignedGroups, legacyAssignedPassport],
  )
}

const needsLookup = (
  group: TGroupReference,
): group is TGroupReference & { id: string } =>
  group.name === undefined && group.id !== undefined

const resolveLegacyAssignedEntity = (
  legacyAssignedPassport: IPassportReference | undefined,
): TUseAlarmAssignedEntitiesResult => {
  if (typeof legacyAssignedPassport?.passportId !== 'string')
    return Loadable.successW([])

  if (typeof legacyAssignedPassport?.userName !== 'string')
    return Loadable.failureW(
      new Error(
        'Reference cannot be discriminated between Group and Passport (has no "userName" property).',
      ),
    )

  if (isGroupName(legacyAssignedPassport.userName))
    return Loadable.successW([
      {
        _tag: 'Group',
        id: legacyAssignedPassport.passportId,
        name: legacyAssignedPassport.userName,
      },
    ])

  return Loadable.successW([
    {
      _tag: 'Passport',
      id: legacyAssignedPassport.passportId,
      username: legacyAssignedPassport.userName,
    },
  ])
}

const toPassport = (
  reference: IPassportReference,
): TPassportReference | null => {
  if (!isNonEmptyString(reference?.passportId)) return null
  if (!isNonEmptyString(reference?.userName)) return null

  return {
    _tag: 'Passport',
    id: reference.passportId,
    username: reference.userName,
  }
}

const toGroup = (entry: string): TGroupReference =>
  isGroupName(entry)
    ? { _tag: 'Group', name: entry }
    : { _tag: 'Group', id: entry }

const dedupeBy = <T>(items: T[], getKey: (it: T) => string): T[] => {
  const seen = new Set<string>()

  return items.filter(it => {
    const key = getKey(it)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const isNotNull = <T>(it: T | null): it is T => it !== null

const isNonEmptyString = (it: unknown): it is string =>
  typeof it === 'string' && it.trim() !== ''

export const isGroupName = (it: string): boolean => /^\/.*\/$/.test(it)
